import React, {useMemo} from 'react'
import type {ManualExamBookletLayoutSettings} from './core/_models'
import {normalizeManualExamLayoutSettings} from './core/_models'

type ManualExamCoverToggleKey =
  | 'showLogo'
  | 'showInstitutionName'
  | 'showFacultyName'
  | 'showDepartmentName'
  | 'showCourseName'
  | 'showSemesterName'
  | 'showExamName'
  | 'showBookletCode'
  | 'showExamDuration'
  | 'showQuestionCount'
  | 'showGlobalExamRules'
  | 'showInstructions'
  | 'showAdditionalNotes'

type ManualExamCoverLabelKey =
  | 'bookletCodeLabel'
  | 'durationLabel'
  | 'questionCountLabel'
  | 'instructionsHeader'
  | 'globalRulesHeader'

type ManualExamCoverFontSizeKey = keyof ManualExamBookletLayoutSettings['cover']['fontSizes']

type ManualExamContentFontSizeKey = keyof ManualExamBookletLayoutSettings['contentFontSizes']

const COVER_FONT_SIZE_FIELDS: ReadonlyArray<{
  key: ManualExamCoverFontSizeKey
  label: string
  description?: string
}> = [
  {key: 'institutionName', label: 'Kurum Adı'},
  {key: 'facultyName', label: 'Fakülte Adı'},
  {key: 'departmentName', label: 'Bölüm Adı'},
  {key: 'courseName', label: 'Ders Adı'},
  {key: 'semesterName', label: 'Yarıyıl Bilgisi'},
  {key: 'examName', label: 'Sınav Adı'},
  {
    key: 'metaInfo',
    label: 'Kitapçık Bilgileri',
    description: 'Kitapçık kodu, sınav süresi ve soru sayısı etiketleri.',
  },
  {key: 'globalRules', label: 'Genel Sınav Kuralları'},
  {key: 'instructionsHeader', label: 'Talimat Başlığı'},
  {key: 'instructionsBody', label: 'Talimat Metni'},
  {key: 'additionalNotes', label: 'Ek Notlar'},
]

const CONTENT_FONT_SIZE_FIELDS: ReadonlyArray<{
  key: ManualExamContentFontSizeKey
  label: string
  description?: string
}> = [
  {key: 'questionText', label: 'Soru Metni'},
  {key: 'optionText', label: 'Seçenek Metni'},
  {
    key: 'numbering',
    label: 'Numaralandırma',
    description: 'Soru numarası ve seçenek harfleri için uygulanır.',
  },
]

type ManualExamLayoutSettingsEditorProps = {
  value: ManualExamBookletLayoutSettings
  onChange: (next: ManualExamBookletLayoutSettings) => void
  disabled?: boolean
}

const COVER_TOGGLES: ReadonlyArray<{
  key: ManualExamCoverToggleKey
  label: string
  description?: string
}> = [
  {key: 'showLogo', label: 'Logo'},
  {key: 'showInstitutionName', label: 'Kurum Adı'},
  {key: 'showFacultyName', label: 'Fakülte Adı'},
  {key: 'showDepartmentName', label: 'Bölüm Adı'},
  {key: 'showCourseName', label: 'Ders Adı'},
  {key: 'showSemesterName', label: 'Yarıyıl Bilgisi'},
  {key: 'showExamName', label: 'Sınav Adı'},
  {key: 'showBookletCode', label: 'Kitapçık Kodu'},
  {key: 'showExamDuration', label: 'Sınav Süresi'},
  {key: 'showQuestionCount', label: 'Soru Sayısı'},
  {
    key: 'showGlobalExamRules',
    label: 'Genel Sınav Kuralları',
    description: 'Tenant ayarlarındaki genel kurallar gösterilir.',
  },
  {
    key: 'showInstructions',
    label: 'Ek Talimatlar',
    description: 'Sınava özel talimat başlığı ve açıklaması gösterilir.',
  },
  {
    key: 'showAdditionalNotes',
    label: 'Ek Notlar',
    description: 'Sınava özel not alanını etkinleştirir.',
  },
]

const COVER_LABELS: Record<ManualExamCoverLabelKey, string> = {
  bookletCodeLabel: 'Kitapçık Kodu Etiketi',
  durationLabel: 'Sınav Süresi Etiketi',
  questionCountLabel: 'Soru Sayısı Etiketi',
  instructionsHeader: 'Talimat Başlığı',
  globalRulesHeader: 'Sınav Kuralları Başlığı',
}

const ManualExamLayoutSettingsEditor: React.FC<ManualExamLayoutSettingsEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const normalizedValue = useMemo(() => normalizeManualExamLayoutSettings(value), [value])

  const updateLayout = (next: ManualExamBookletLayoutSettings) => {
    onChange(normalizeManualExamLayoutSettings(next))
  }

  const handleToggleFrontCover = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked
    updateLayout({
      ...normalizedValue,
      printFrontCover: nextValue,
      insertBlankPageAfterCover: nextValue ? normalizedValue.insertBlankPageAfterCover : false,
    })
  }

  const handleToggleBlankPageAfterCover = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!normalizedValue.printFrontCover) {
      return
    }

    updateLayout({
      ...normalizedValue,
      insertBlankPageAfterCover: event.target.checked,
    })
  }

  const updateCover = (updates: Partial<ManualExamBookletLayoutSettings['cover']>) => {
    updateLayout({
      ...normalizedValue,
      cover: {
        ...normalizedValue.cover,
        ...updates,
      },
    })
  }

  const updateBackCover = (updates: Partial<ManualExamBookletLayoutSettings['backCover']>) => {
    updateLayout({
      ...normalizedValue,
      backCover: {
        ...normalizedValue.backCover,
        ...updates,
      },
    })
  }

  const handleMarginChange =
    (key: keyof ManualExamBookletLayoutSettings['pageMargins']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateLayout({
        ...normalizedValue,
        pageMargins: {
          ...normalizedValue.pageMargins,
          [key]: Number(event.target.value) || normalizedValue.pageMargins[key],
        },
      })
    }

  const handleCoverToggle =
    (key: ManualExamCoverToggleKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateCover({[key]: event.target.checked} as Partial<
        ManualExamBookletLayoutSettings['cover']
      >)
    }

  const handleCoverLabelChange =
    (key: ManualExamCoverLabelKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateCover({[key]: event.target.value} as Partial<ManualExamBookletLayoutSettings['cover']>)
    }

  const handleLogoBase64Change = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const raw = event.target.value ?? ''
    const trimmed = raw.trim()
    updateCover({logoBase64: trimmed.length > 0 ? trimmed : null})
  }

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result.trim() : ''
      updateCover({logoBase64: result.length > 0 ? result : null})
    }
    reader.onerror = () => {
      updateCover({logoBase64: null})
    }
    reader.readAsDataURL(file)
    event.target.value = ''
  }

  const handleLogoClear = () => {
    updateCover({logoBase64: null})
  }

  const handleLogoDimensionChange =
    (key: 'logoWidth' | 'logoHeight') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value
      const parsed = Number(raw)
      const nextValue =
        raw === '' || !Number.isFinite(parsed) ? normalizedValue.cover[key] : parsed
      updateCover({[key]: nextValue} as Partial<ManualExamBookletLayoutSettings['cover']>)
    }

  const handleAdditionalNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateCover({additionalNotes: event.target.value})
  }

  const handleBackCoverToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateBackCover({enabled: event.target.checked})
  }

  const handleBackCoverContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateBackCover({content: event.target.value})
  }

  const handleCoverFontSizeChange =
    (key: ManualExamCoverFontSizeKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value
      const parsed = Number(raw)
      const nextValue =
        raw === '' || !Number.isFinite(parsed) ? normalizedValue.cover.fontSizes[key] : parsed
      updateCover({
        fontSizes: {
          ...normalizedValue.cover.fontSizes,
          [key]: nextValue,
        },
      })
    }

  const handleBackCoverFontSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const parsed = Number(raw)
    const nextValue =
      raw === '' || !Number.isFinite(parsed) ? normalizedValue.backCover.fontSize : parsed
    updateBackCover({fontSize: nextValue})
  }

  const handleContentFontSizeChange =
    (key: ManualExamContentFontSizeKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value
      const parsed = Number(raw)
      const nextValue =
        raw === '' || !Number.isFinite(parsed)
          ? normalizedValue.contentFontSizes[key]
          : parsed
      updateLayout({
        ...normalizedValue,
        contentFontSizes: {
          ...normalizedValue.contentFontSizes,
          [key]: nextValue,
        },
      })
    }

  const coverControlsDisabled = disabled || !normalizedValue.printFrontCover
  const coverLogoEnabled = normalizedValue.cover.showLogo && !coverControlsDisabled
  const currentLogo = normalizedValue.cover.logoBase64 ?? ''

  return (
    <div className='border rounded-3 p-4 bg-light'>
      <div className='row g-4'>
        <div className='col-md-6'>
          <h6 className='fw-semibold mb-3'>Sayfa Kenar Boşlukları (mm)</h6>
          <div className='row g-3'>
            {(
              [
                {key: 'top', label: 'Üst'},
                {key: 'bottom', label: 'Alt'},
                {key: 'left', label: 'Sol'},
                {key: 'right', label: 'Sağ'},
                {key: 'coverTop', label: 'Kapak Üst'},
              ] as const
            ).map(({key, label}) => (
              <div className='col-sm-6' key={key}>
                <label className='form-label fw-semibold'>{label}</label>
                <input
                  type='number'
                  className='form-control form-control-sm'
                  value={normalizedValue.pageMargins[key]}
                  onChange={handleMarginChange(key)}
                  min={10}
                  max={72}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        </div>
        <div className='col-md-6'>
          <h6 className='fw-semibold mb-3'>Kapak İçeriği</h6>
          <div className='border rounded-3 p-3 bg-white shadow-sm mb-3'>
            <div className='form-check form-switch form-check-custom form-check-solid mb-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='front-cover-enabled'
                checked={normalizedValue.printFrontCover}
                onChange={handleToggleFrontCover}
                disabled={disabled}
              />
              <label className='form-check-label fw-semibold' htmlFor='front-cover-enabled'>
                Ön kapak bas
              </label>
              <div className='form-text'>Kapak içeriğini basılı kitapçığa ekler.</div>
            </div>
            <div className='form-check form-switch form-check-custom form-check-solid mb-0'>
              <input
                className='form-check-input'
                type='checkbox'
                id='insert-blank-page-after-cover'
                checked={normalizedValue.insertBlankPageAfterCover}
                onChange={handleToggleBlankPageAfterCover}
                disabled={disabled || !normalizedValue.printFrontCover}
              />
              <label
                className='form-check-label fw-semibold'
                htmlFor='insert-blank-page-after-cover'
              >
                Kapaktan sonra boş sayfa ekle
              </label>
              <div className='form-text'>Kapak sonrasında tek yaprak boş sayfa bırakılır.</div>
            </div>
          </div>
          <div className='border rounded-3 p-3 bg-white shadow-sm mb-3'>
            <div className='form-check form-switch form-check-custom form-check-solid mb-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='cover-show-logo'
                checked={normalizedValue.cover.showLogo}
                onChange={handleCoverToggle('showLogo')}
                disabled={coverControlsDisabled}
              />
              <label className='form-check-label fw-semibold' htmlFor='cover-show-logo'>
                Logo
              </label>
            </div>
            <label className='form-label fw-semibold'>Logo (Base64)</label>
            <textarea
              className='form-control form-control-sm'
              rows={3}
              value={currentLogo}
              onChange={handleLogoBase64Change}
              disabled={!coverLogoEnabled}
              placeholder='data:image/png;base64,...'
            />
            <div className='d-flex flex-wrap gap-2 mt-2'>
              <label className={`btn btn-sm btn-light mb-0 ${coverLogoEnabled ? '' : 'disabled'}`}>
                Dosya Seç
                <input
                  type='file'
                  accept='image/*'
                  hidden
                  onChange={handleLogoUpload}
                  disabled={!coverLogoEnabled}
                />
              </label>
              <button
                type='button'
                className='btn btn-sm btn-light-danger'
                onClick={handleLogoClear}
                disabled={coverControlsDisabled}
              >
                Logoyu Kaldır
              </button>
            </div>
            <div className='form-text'>
              Logo base64 formatında saklanır. Boş bırakılırsa kurum logosu kullanılır.
            </div>
            <div className='row g-3 mt-2'>
              <div className='col-sm-6'>
                <label className='form-label fw-semibold'>Logo Genişliği (px)</label>
                <input
                  type='number'
                  className='form-control form-control-sm'
                  min={40}
                  max={320}
                  step={1}
                  value={normalizedValue.cover.logoWidth}
                  onChange={handleLogoDimensionChange('logoWidth')}
                  disabled={!coverLogoEnabled}
                />
              </div>
              <div className='col-sm-6'>
                <label className='form-label fw-semibold'>Logo Yüksekliği (px)</label>
                <input
                  type='number'
                  className='form-control form-control-sm'
                  min={40}
                  max={240}
                  step={1}
                  value={normalizedValue.cover.logoHeight}
                  onChange={handleLogoDimensionChange('logoHeight')}
                  disabled={!coverLogoEnabled}
                />
              </div>
            </div>
            <div className='form-text'>
              Logo önizlemesinde kullanılan maksimum genişlik ve yükseklik değerleri ayarlanabilir.
            </div>
            {currentLogo && (
              <div className='mt-3 text-center'>
                <img
                  src={currentLogo}
                  alt='Kapak logo önizlemesi'
                  className='img-fluid border rounded-3 bg-light p-2'
                  style={{
                    maxHeight: normalizedValue.cover.logoHeight,
                    maxWidth: normalizedValue.cover.logoWidth,
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
          </div>
          <div className='row g-3'>
            {COVER_TOGGLES.filter((toggle) => toggle.key !== 'showLogo').map((toggle) => (
              <div className='col-sm-6' key={toggle.key}>
                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id={`cover-toggle-${toggle.key}`}
                    checked={!!normalizedValue.cover[toggle.key]}
                    onChange={handleCoverToggle(toggle.key)}
                    disabled={coverControlsDisabled}
                  />
                  <label
                    className='form-check-label fw-semibold'
                    htmlFor={`cover-toggle-${toggle.key}`}
                  >
                    {toggle.label}
                  </label>
                  {toggle.description && <div className='form-text'>{toggle.description}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className='mt-4'>
            <h6 className='fw-semibold mb-3'>Yazı Tipi Boyutları (pt)</h6>
            <div className='row g-3'>
              {COVER_FONT_SIZE_FIELDS.map((item) => (
                <div className='col-sm-6' key={item.key}>
                  <label className='form-label fw-semibold'>{item.label}</label>
                  <input
                    type='number'
                    className='form-control form-control-sm'
                    min={8}
                    max={48}
                    step={0.5}
                    value={normalizedValue.cover.fontSizes[item.key]}
                    onChange={handleCoverFontSizeChange(item.key)}
                    disabled={coverControlsDisabled}
                  />
                  {item.description && <div className='form-text'>{item.description}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className='row g-4 mt-4'>
        <div className='col-md-6'>
          <h6 className='fw-semibold mb-3'>Kapak Etiketleri</h6>
          <div className='d-flex flex-column gap-3'>
            {(Object.keys(COVER_LABELS) as ManualExamCoverLabelKey[]).map((key) => (
              <div key={key}>
                <label className='form-label fw-semibold'>{COVER_LABELS[key]}</label>
                <input
                  className='form-control form-control-sm'
                  value={normalizedValue.cover[key] ?? ''}
                  onChange={handleCoverLabelChange(key)}
                  disabled={coverControlsDisabled}
                />
              </div>
            ))}
          </div>
          {normalizedValue.cover.showAdditionalNotes && (
            <div className='mt-4'>
              <label className='form-label fw-semibold'>Ek Notlar</label>
              <textarea
                className='form-control form-control-sm'
                rows={5}
                value={normalizedValue.cover.additionalNotes ?? ''}
                onChange={handleAdditionalNotesChange}
                disabled={coverControlsDisabled}
              />
              <div className='form-text'>
                Bu alan sınav kitapçığında düz metin olarak gösterilir.
              </div>
            </div>
          )}
        </div>
        <div className='col-md-6'>
          <div className='form-check form-switch form-check-custom form-check-solid mb-3'>
            <input
              className='form-check-input'
              type='checkbox'
            id='back-cover-enabled'
            checked={normalizedValue.backCover.enabled}
            onChange={handleBackCoverToggle}
            disabled={disabled}
          />
          <label className='form-check-label fw-semibold' htmlFor='back-cover-enabled'>
            Arka kapak bas
          </label>
        </div>
          {normalizedValue.backCover.enabled && (
            <div>
              <label className='form-label fw-semibold'>Arka Kapak İçeriği</label>
              <div className='row g-3 mb-3'>
                <div className='col-sm-6'>
                  <label className='form-label fw-semibold'>Yazı Tipi Boyutu (pt)</label>
                  <input
                    type='number'
                    className='form-control form-control-sm'
                    min={8}
                    max={48}
                    step={0.5}
                    value={normalizedValue.backCover.fontSize}
                    onChange={handleBackCoverFontSizeChange}
                    disabled={disabled}
                  />
                </div>
              </div>
              <textarea
                className='form-control form-control-sm'
                rows={6}
                value={normalizedValue.backCover.content ?? ''}
                onChange={handleBackCoverContentChange}
                disabled={disabled}
              />
              <div className='form-text'>Düz metin olarak saklanır ve arka kapakta gösterilir.</div>
            </div>
          )}
      </div>
    </div>

      <div className='mt-4'>
        <h6 className='fw-semibold mb-3'>Soru Sayfası Yazı Tipi Boyutları (pt)</h6>
        <div className='row g-3'>
          {CONTENT_FONT_SIZE_FIELDS.map((item) => (
            <div className='col-sm-6 col-lg-4' key={item.key}>
              <label className='form-label fw-semibold'>{item.label}</label>
              <input
                type='number'
                className='form-control form-control-sm'
                min={8}
                max={48}
                step={0.5}
                value={normalizedValue.contentFontSizes[item.key]}
                onChange={handleContentFontSizeChange(item.key)}
                disabled={disabled}
              />
              {item.description && <div className='form-text'>{item.description}</div>}
            </div>
          ))}
        </div>
        <div className='form-text'>
          Bu ayarlar soru metinlerini, seçenekleri ve numaralandırmaları etkiler.
        </div>
      </div>
    </div>
  )
}

export default ManualExamLayoutSettingsEditor
